import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CircularProgress, Dialog as MuiDialog } from "@mui/material"
import type { PopoverOrigin } from "@mui/material"
import { format } from "date-fns"

import BasicButton from "@/components/button/BasicButton"
import Chip from "@/components/chip/Chip"
import Dialog from "@/components/dialog/Dialog"
import Icon from "@/components/icon/Icon"
import useRouter from "@/hooks/router/useRoute"
import {
  useActivityDetailQuery,
  useAnalyzingActivityQuery,
  useDeleteActivityMutation,
} from "@/hooks/api/useActivityQuery"
import { useSeasonWeekQuery, usePlayerGroupsQuery } from "@/hooks/api/useSettingQuery"
import {
  buildDailyReportUrl,
  getButtonDisabledByStatus,
  getViewReportButtonText,
} from "@/app/activity/list/utils"
import { getImagePath } from "@/utils/image"
import type { Activity } from "@/types/activity"
import { ActivityType } from "@/types/activity"
import { ReportStatus } from "@/types/report"

import "./activityInfoDialog.scss"

interface ActivityInfoDialogProps {
  open: boolean
  // anchorEl/anchorOrigin/transformOrigin 은 모달 전환 후 사용 안 함 (호출측 호환 위해 시그니처 유지)
  anchorEl?: HTMLElement | null
  activity: Pick<Activity, "idfActivity" | "name">
  onClose: () => void
  onEdit: (activity: Activity) => void
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
}

export default function ActivityInfoDialog({ open, activity, onClose }: ActivityInfoDialogProps) {
  const { t } = useTranslation()
  const router = useRouter()

  const idfActivity = activity.idfActivity as number
  const { data: detail, isLoading } = useActivityDetailQuery(idfActivity, open && !!idfActivity)
  const { data: activityStatus } = useAnalyzingActivityQuery(idfActivity, open)
  const { data: seasonWeekData } = useSeasonWeekQuery()
  const { data: playerGroupData } = usePlayerGroupsQuery()

  const { mutate: deleteActivity, isPending: isDeleting } = useDeleteActivityMutation(idfActivity)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const seasonNameById = useMemo(() => {
    const map = new Map<number, string>()
    ;(seasonWeekData?.seasonWeeks ?? []).forEach((sw) => {
      if (sw.idfSeasonWeek != null) {
        map.set(sw.idfSeasonWeek, sw.name ?? `#${sw.idfSeasonWeek}`)
      }
    })

    return map
  }, [seasonWeekData])

  const playerGroupNameById = useMemo(() => {
    const map = new Map<number, string>()
    ;(playerGroupData?.teamPlayerGroups ?? []).forEach((g) => {
      if (g.idfTeamPlayerGroup != null) {
        map.set(g.idfTeamPlayerGroup, g.name ?? `Group ${g.idfTeamPlayerGroup}`)
      }
    })

    return map
  }, [playerGroupData])

  const meta = useMemo(() => {
    if (!detail) {
      return null
    }
    const start = new Date(detail.dateStart)
    const finish = new Date(detail.dateFinish)
    if (Number.isNaN(start.getTime()) || Number.isNaN(finish.getTime())) {
      return null
    }
    const dateLabel = format(start, "yyyy-MM-dd EEE").toUpperCase()
    const startTime = format(start, "HH:mm:ss")
    const finishTime = format(finish, "HH:mm:ss")

    const seasonId = (detail as { idfSeasonWeek?: number | null }).idfSeasonWeek
    const seasonLabel = seasonId != null ? (seasonNameById.get(seasonId) ?? "-") : "-"

    return {
      typeLabel: ActivityType[detail.type as keyof typeof ActivityType] ?? detail.type,
      season: seasonLabel,
      matchDay: detail.matchDay ?? "-",
      duration: `${dateLabel} ${startTime} ~ ${finishTime}`,
    }
  }, [detail, seasonNameById])

  const handleActivityDetails = () => {
    onClose()
    router.push(`/activity/detail?activity=${idfActivity}`)
  }

  const handleViewReport = () => {
    onClose()
    router.push(buildDailyReportUrl(idfActivity, detail))
  }

  const handleDelete = () => {
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    deleteActivity(idfActivity, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        onClose()
      },
    })
  }

  const sessions = detail?.sessions ?? []
  const players = detail?.players ?? []

  // detail.sessions[].playerCount 가 0/undefined 로 내려와 — 각 player.participatedSessions 로 직접 카운트
  const sessionPlayerCount = useMemo(() => {
    const map = new Map<number, number>()
    players.forEach((p) => {
      ;(p.participatedSessions ?? []).forEach((sid) => {
        map.set(sid, (map.get(sid) ?? 0) + 1)
      })
    })

    return map
  }, [players])

  return (
    <>
      <MuiDialog
        open={open}
        onClose={onClose}
        PaperProps={{ className: "ActivityInfoDialog__paper" }}>
        <div className="ActivityInfoDialog">
          <div className="ActivityInfoDialog__header">
            <h2 className="ActivityInfoDialog__title">{detail?.name ?? activity.name}</h2>
            <button
              type="button"
              className="ActivityInfoDialog__close"
              aria-label="Close"
              onClick={onClose}>
              <Icon type="close" />
            </button>
          </div>

          {isLoading && !detail ? (
            <div className="ActivityInfoDialog__loader">
              <CircularProgress size={20} />
            </div>
          ) : (
            <div className="ActivityInfoDialog__body">
              <div className="ActivityInfoDialog__meta">
                <MetaCell
                  label={t("activityType", "Activity Type")}
                  value={t(meta?.typeLabel ?? "-")}
                />
                <MetaCell label={t("season", "Season")} value={meta?.season ?? "-"} />
                <MetaCell label={t("matchDay", "Match Day")} value={meta?.matchDay ?? "-"} />
                <MetaCell label={t("duration", "Duration")} value={meta?.duration ?? "-"} wide />
              </div>

              {detail?.memo && (
                <Section label={t("memo", "Memo")}>
                  <p className="ActivityInfoDialog__memo-text">{detail.memo}</p>
                </Section>
              )}

              {detail?.stadium && (
                <Section label={t("location", "Location")}>
                  <p className="ActivityInfoDialog__location-text">{detail.stadium}</p>
                </Section>
              )}

              <div className="ActivityInfoDialog__grid">
                <Card title={t("session", "Session")} count={sessions.length}>
                  <ul className="ActivityInfoDialog__session-list">
                    {sessions.length === 0 && (
                      <li className="ActivityInfoDialog__empty">
                        {t("noSessions", "No sessions")}
                      </li>
                    )}
                    {sessions.map((s) => {
                      const sStart = new Date(s.dateStart)
                      const sFinish = new Date(s.dateFinish)
                      const validTime =
                        !Number.isNaN(sStart.getTime()) && !Number.isNaN(sFinish.getTime())
                      const timeLabel = validTime
                        ? `${format(sStart, "HH:mm:ss")} ~ ${format(sFinish, "HH:mm:ss")}`
                        : "--:--:-- ~ --:--:--"
                      // duration: dateFinish - dateStart 가 진실. s.duration 은 단위가 모호해 미사용
                      const durationLabel = validTime
                        ? formatMmSs(Math.max(0, sFinish.getTime() - sStart.getTime()))
                        : "--' --\""

                      return (
                        <li key={s.idfSession} className="ActivityInfoDialog__session-item">
                          <div className="ActivityInfoDialog__session-text">
                            <span className="ActivityInfoDialog__session-name">
                              {s.name ?? `Session ${s.idfSession}`}
                            </span>
                            <span className="ActivityInfoDialog__session-time">{timeLabel}</span>
                          </div>
                          <div className="ActivityInfoDialog__session-meta">
                            <span className="ActivityInfoDialog__session-count">
                              <Icon type="user-line" size={12} />
                              {sessionPlayerCount.get(s.idfSession) ?? s.playerCount ?? 0}
                            </span>
                            <span className="ActivityInfoDialog__session-duration">
                              {durationLabel}
                            </span>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </Card>

                <Card title={t("player", "Player")} count={players.length}>
                  <ul className="ActivityInfoDialog__player-list">
                    {players.length === 0 && (
                      <li className="ActivityInfoDialog__empty">{t("noPlayers", "No players")}</li>
                    )}
                    {players.map((p) => {
                      const groupName = playerGroupNameById.get(p.idfPlayerGroup as number)
                      const subParts = [p.position, groupName].filter(Boolean) as string[]

                      return (
                        <li key={p.idfPlayer} className="ActivityInfoDialog__player-item">
                          <div className="ActivityInfoDialog__player-avatar">
                            {p.image ? (
                              <img src={getImagePath(p.image)} alt={p.name ?? p.nickname ?? ""} />
                            ) : (
                              <Icon type="no-profile" />
                            )}
                          </div>
                          <div className="ActivityInfoDialog__player-text">
                            <div className="ActivityInfoDialog__player-inner">
                              <span className="ActivityInfoDialog__player-number">
                                {p.uniformNumber ?? "-"}
                              </span>
                              <span className="ActivityInfoDialog__player-name">
                                {p.nickname || p.name || "-"}
                              </span>
                            </div>
                            {subParts.length > 0 && (
                              <span className="ActivityInfoDialog__player-sub">
                                {subParts.join(" · ")}
                              </span>
                            )}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </Card>
              </div>
            </div>
          )}

          <div className="ActivityInfoDialog__actions">
            <BasicButton
              size="sm"
              type="danger-outlined"
              onClick={handleDelete}
              isLoading={isDeleting}>
              {t("button.delete", "Delete")}
            </BasicButton>
            <div className="ActivityInfoDialog__actions-right">
              <BasicButton size="sm" type="default-outlined" onClick={handleActivityDetails}>
                {t("activityDetails", "Activity Details")}
              </BasicButton>
              <BasicButton
                size="sm"
                type="primary-contained"
                disabled={getButtonDisabledByStatus(activityStatus?.status as ReportStatus)}
                onClick={handleViewReport}>
                {getViewReportButtonText(activityStatus?.status as ReportStatus, t)}
              </BasicButton>
            </div>
          </div>
        </div>
      </MuiDialog>

      <Dialog
        title={t("confirm")}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="400px"
        footer={
          <>
            <BasicButton
              type="default-outlined"
              size="md"
              onClick={() => setDeleteDialogOpen(false)}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton
              type="danger-outlined"
              size="md"
              onClick={handleConfirmDelete}
              isLoading={isDeleting}>
              {t("button.delete")}
            </BasicButton>
          </>
        }>
        <p>{t("messages.confirmDelete")}</p>
      </Dialog>
    </>
  )
}

function MetaCell({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div
      className={`ActivityInfoDialog__meta-cell${
        wide ? " ActivityInfoDialog__meta-cell--wide" : ""
      }`}>
      <span className="ActivityInfoDialog__meta-label">{label}</span>
      <span className="ActivityInfoDialog__meta-value">{value}</span>
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="ActivityInfoDialog__section">
      <span className="ActivityInfoDialog__section-label">{label}</span>
      {children}
    </div>
  )
}

function Card({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div className="ActivityInfoDialog__card">
      <div className="ActivityInfoDialog__card-header">
        <span className="ActivityInfoDialog__card-title">{title}</span>
        <Chip type="number" label={count} />
      </div>
      <div className="ActivityInfoDialog__card-body">{children}</div>
    </div>
  )
}

// 세션 duration → "MM' SS"" — 시안의 우측 하단 표기 형식
function formatMmSs(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n))

  return `${pad(m)}' ${pad(s)}"`
}
